import React, { useState, useEffect, useRef, useMemo, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import moment from "moment";
import { useTranslation } from "react-i18next";

/**
 * Reusable Calendar Component with Custom Format Support
 * 
 * @param {Object} props
 * @param {string} props.value - The date value (will be parsed based on format)
 * @param {Function} props.onChange - Callback function that receives the date in the specified format
 * @param {string} props.format - Date format: "YYYY-MM-DD" | "MM-DD-YYYY" | "DD-MM-YYYY" (default: "YYYY-MM-DD")
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.id - Input id
 * @param {string} props.name - Input name
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.disabled - Whether the input is disabled
 * @param {string} props.min - Minimum date (will be parsed based on format)
 * @param {string} props.max - Maximum date (will be parsed based on format)
 * @param {boolean} props.required - Whether the input is required
 */
export default function Calendar({
    value = "",
    onChange,
    format = "YYYY-MM-DD",
    placeholder = "Seleccione una fecha",
    id = "",
    name = "",
    className = "",
    disabled = false,
    min = "",
    max = "",
    required = false,
}) {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [currentMonth, setCurrentMonth] = useState(moment());
    const [inputValue, setInputValue] = useState("");
    const [isValid, setIsValid] = useState(true);
    const [hasBlurred, setHasBlurred] = useState(false);
    const calendarRef = useRef(null);
    const inputRef = useRef(null);
    const isInternalChangeRef = useRef(false);
    /** Viewport position for the popover when portaled to `document.body` (like react-select `menuPortalTarget`). */
    const [dropdownRect, setDropdownRect] = useState(null);

    const updateDropdownPosition = useCallback(() => {
        if (!isOpen) return;
        const el = inputRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const viewportW = window.innerWidth || 0;
        const viewportH = window.innerHeight || 0;
        const gap = 8;
        const estimatedDropdownH = 430; // calendar panel height estimate for flip logic
        const preferredMinWidth = Math.max(280, rect.width);

        const left = Math.max(
            gap,
            Math.min(rect.left, viewportW - preferredMinWidth - gap)
        );

        let top = rect.bottom + gap;
        if (top + estimatedDropdownH > viewportH - gap) {
            top = Math.max(gap, rect.top - estimatedDropdownH - gap);
        }

        setDropdownRect({
            top,
            left,
            minWidth: preferredMinWidth,
        });
    }, [isOpen]);

    // Parse date value based on format (accepts both dashes and slashes)
    const parseDate = (dateString) => {
        if (!dateString) return null;
        // Try formats with dashes and slashes
        const formats = [
            "YYYY-MM-DD", "MM-DD-YYYY", "DD-MM-YYYY",
            "YYYY/MM/DD", "MM/DD/YYYY", "DD/MM/YYYY"
        ];
        for (const fmt of formats) {
            const parsed = moment(dateString, fmt, true);
            if (parsed.isValid()) {
                return parsed;
            }
        }
        return null;
    };

    // Format date according to specified format
    const formatDate = (date) => {
        if (!date) return "";
        return moment(date).format(format);
    };

    // Validate date format (accepts both dashes and slashes)
    const validateDate = (dateString) => {
        if (!dateString || dateString.trim() === "") {
            return { isValid: true, date: null }; // Empty is valid (not required validation)
        }

        // Convert format to also check with slashes
        const formatWithSlashes = format.replace(/-/g, "/");

        // Try to parse with the specified format (both dashes and slashes)
        let parsed = moment(dateString, format, true);
        if (!parsed.isValid()) {
            parsed = moment(dateString, formatWithSlashes, true);
        }
        if (parsed.isValid()) {
            return { isValid: true, date: parsed };
        }

        // Also check other common formats with both dashes and slashes
        const formats = [
            "YYYY-MM-DD", "MM-DD-YYYY", "DD-MM-YYYY",
            "YYYY/MM/DD", "MM/DD/YYYY", "DD/MM/YYYY"
        ];
        for (const fmt of formats) {
            const testParsed = moment(dateString, fmt, true);
            if (testParsed.isValid()) {
                return { isValid: true, date: testParsed };
            }
        }

        return { isValid: false, date: null };
    };

    // Derive selectedDate directly from value prop (no state syncing needed)
    const selectedDate = useMemo(() => {
        if (!value) return null;
        const parsed = parseDate(value);
        return parsed && parsed.isValid() ? parsed : null;
    }, [value, format]);

    // Sync inputValue and currentMonth with value prop (skip if change is internal)
    useEffect(() => {
        if (isInternalChangeRef.current) {
            isInternalChangeRef.current = false;
            return;
        }

        if (value) {
            const parsed = parseDate(value);
            if (parsed && parsed.isValid()) {
                setCurrentMonth(parsed.clone());
                setInputValue(formatDate(parsed));
                setIsValid(true);
            } else {
                setInputValue(value);
            }
        } else {
            setInputValue("");
            setIsValid(true);
        }
    }, [value, format]);

    useLayoutEffect(() => {
        if (!isOpen || disabled) {
            setDropdownRect(null);
            return;
        }
        updateDropdownPosition();
    }, [isOpen, disabled, updateDropdownPosition]);

    useEffect(() => {
        if (!isOpen) return;
        const update = () => updateDropdownPosition();
        window.addEventListener("scroll", update, true);
        window.addEventListener("resize", update);
        return () => {
            window.removeEventListener("scroll", update, true);
            window.removeEventListener("resize", update);
        };
    }, [isOpen, updateDropdownPosition]);

    // Close calendar when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                calendarRef.current &&
                !calendarRef.current.contains(event.target) &&
                inputRef.current &&
                !inputRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    // Get days of the month
    const getDaysInMonth = () => {
        const startOfMonth = currentMonth.clone().startOf("month");
        const endOfMonth = currentMonth.clone().endOf("month");
        const startDate = startOfMonth.clone().startOf("week");
        const endDate = endOfMonth.clone().endOf("week");
        const days = [];
        let currentDate = startDate.clone();

        while (currentDate.isSameOrBefore(endDate, "day")) {
            days.push(currentDate.clone());
            currentDate.add(1, "day");
        }

        return days;
    };

    // Check if date is disabled
    const isDateDisabled = (date) => {
        if (min) {
            const minDate = parseDate(min);
            if (minDate && date.isBefore(minDate, "day")) return true;
        }
        if (max) {
            const maxDate = parseDate(max);
            if (maxDate && date.isAfter(maxDate, "day")) return true;
        }
        return false;
    };

    // Handle date selection
    const handleDateSelect = (date) => {
        // console.log('handleDateSelect called with:', date, 'formatted:', date ? date.format('YYYY-MM-DD') : 'null');

        if (!date || !date.isValid()) {
            console.error('Invalid date passed to handleDateSelect');
            return;
        }

        if (isDateDisabled(date)) {
            console.log('Date is disabled, returning');
            return;
        }

        // Clone the date to ensure we're working with a fresh moment object
        const dateClone = date.clone();

        // Format the date immediately - FORCE this to be the new date
        const formattedDate = formatDate(dateClone);
        // console.log('Formatted date:', formattedDate);

        // FORCE onChange to be called FIRST with the new date - before any state updates
        // This ensures the parent gets the new value immediately
        if (onChange && formattedDate) {
            // console.log('Calling onChange with:', formattedDate);
            onChange(formattedDate);
        } else {
            console.warn('onChange not available or formattedDate is empty', { onChange: !!onChange, formattedDate });
        }

        // Then update internal state
        isInternalChangeRef.current = true;
        setCurrentMonth(dateClone);
        setInputValue(formattedDate);

        // Close calendar
        setIsOpen(false);
    };

    // Navigate months
    const handlePreviousMonth = () => {
        setCurrentMonth(currentMonth.clone().subtract(1, "month"));
    };

    const handleNextMonth = () => {
        setCurrentMonth(currentMonth.clone().add(1, "month"));
    };

    // Navigate to today
    const handleToday = () => {
        const today = moment();
        if (!isDateDisabled(today)) {
            setCurrentMonth(today.clone());
            handleDateSelect(today);
        }
    };

    // Handle manual input change
    const handleInputChange = (e) => {
        const newValue = e.target.value;
        setInputValue(newValue);
        setHasBlurred(false);
        setIsValid(true); // Clear validation while typing
    };

    // Handle input blur - validate on blur
    const handleInputBlur = () => {
        setHasBlurred(true);
        const validation = validateDate(inputValue);

        if (validation.isValid && validation.date) {
            const formattedDate = formatDate(validation.date);
            isInternalChangeRef.current = true;
            setCurrentMonth(validation.date.clone());
            setInputValue(formattedDate);
            setIsValid(true);
            if (onChange) {
                onChange(formattedDate);
            }
        } else if (inputValue.trim() === "") {
            // Empty is valid if not required
            isInternalChangeRef.current = true;
            setIsValid(true);
            if (onChange) {
                onChange("");
            }
        } else {
            setIsValid(false);
        }
    };

    // Handle Enter key - trigger validation and close calendar
    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            setIsOpen(false); // Close the calendar
            inputRef.current?.blur(); // This will trigger handleInputBlur
        }
    };


    const days = getDaysInMonth();
    const weekDays = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

    return (
        <div className={`relative w-full ${className}`}>
            <div className="relative">
                <input
                    ref={inputRef}
                    type="text"
                    id={id}
                    name={name}
                    value={inputValue}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="none"
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    disabled={disabled}
                    required={required}
                    onClick={() => !disabled && setIsOpen(!isOpen)}
                    className={`w-full px-3 py-2 pr-10 border rounded-lg focus:outline-none focus:ring-2 bg-white text-gray-700 transition-all duration-200 ${disabled
                        ? "bg-gray-100 cursor-not-allowed"
                        : "cursor-pointer hover:border-[#84B044]"
                        } ${hasBlurred && !isValid
                            ? "border-red-500 focus:ring-red-500"
                            : isOpen
                                ? "border-[#84B044] ring-2 ring-[#84B044] focus:ring-[#84B044]"
                                : "border-gray-300 focus:ring-[#84B044]"
                        }`}
                />
                <div
                    className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
                    onClick={() => !disabled && setIsOpen(!isOpen)}
                >
                    <CalendarIcon
                        className={`h-5 w-5 transition-colors duration-200 ${isOpen ? "text-[#84B044]" : "text-gray-400"
                            }`}
                    />
                </div>
            </div>

            {/* Error message */}
            {hasBlurred && !isValid && (
                <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                    <span>{t("alert_fecha_invalida")}</span>
                </p>
            )}

            {isOpen &&
                !disabled &&
                dropdownRect &&
                typeof document !== "undefined" &&
                createPortal(
                    <div
                        ref={calendarRef}
                        className="bg-white rounded-lg shadow-xl border border-gray-200 p-4 transform transition-all duration-200 ease-out opacity-100 scale-100 w-full sm:w-auto"
                        style={{
                            position: "fixed",
                            top: dropdownRect.top,
                            left: dropdownRect.left,
                            minWidth: dropdownRect.minWidth,
                            maxWidth: "calc(100vw - 16px)",
                            zIndex: 999,
                        }}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between mb-4">
                            <button
                                type="button"
                                onClick={handlePreviousMonth}
                                className="p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200 group"
                                aria-label="Previous month"
                            >
                                <ChevronLeft className="h-5 w-5 text-gray-600 group-hover:text-[#84B044] transition-colors" />
                            </button>
                            <div className="flex items-center gap-2">
                                <h3 className="text-lg font-semibold text-gray-800">
                                    {currentMonth.format("MMMM YYYY")}
                                </h3>
                            </div>
                            <button
                                type="button"
                                onClick={handleNextMonth}
                                className="p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200 group"
                                aria-label="Next month"
                            >
                                <ChevronRight className="h-5 w-5 text-gray-600 group-hover:text-[#84B044] transition-colors" />
                            </button>
                        </div>

                        {/* Week days */}
                        <div className="grid grid-cols-7 gap-1 mb-2">
                            {weekDays.map((day, index) => (
                                <div
                                    key={index}
                                    className="text-center text-sm font-medium text-gray-600 py-2"
                                >
                                    {day}
                                </div>
                            ))}
                        </div>

                        {/* Calendar days */}
                        <div className="grid grid-cols-7 gap-1">
                            {days.map((day, index) => {
                                const isCurrentMonth = day.isSame(currentMonth, "month");
                                const isSelected = selectedDate && day.isSame(selectedDate, "day");
                                const isToday = day.isSame(moment(), "day");
                                const isDisabled = isDateDisabled(day);

                                return (
                                    <button
                                        key={index}
                                        type="button"
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                        }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            // console.log('Button clicked, day:', day.format('YYYY-MM-DD'));
                                            const dayClone = day.clone();
                                            handleDateSelect(dayClone);
                                        }}
                                        disabled={isDisabled}
                                        className={`
                                        aspect-square p-2 text-sm rounded-lg transition-all duration-200
                                        ${!isCurrentMonth ? "text-gray-300" : "text-gray-700"}
                                        ${isSelected
                                                ? "bg-[#84B044] text-white font-semibold shadow-md scale-105"
                                                : isToday
                                                    ? "bg-blue-50 text-blue-600 font-semibold border-2 border-blue-300"
                                                    : "hover:bg-gray-100"
                                            }
                                        ${isDisabled
                                                ? "opacity-50 cursor-not-allowed hover:bg-transparent"
                                                : "cursor-pointer hover:scale-105"
                                            }
                                        ${isSelected && isToday ? "ring-2 ring-blue-400" : ""}
                                    `}
                                    >
                                        {day.format("D")}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Today button */}
                        <div className="mt-4 pt-4 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={handleToday}
                                disabled={isDateDisabled(moment())}
                                className="w-full px-4 py-2 text-sm font-medium text-[#84B044] bg-green-50 rounded-lg hover:bg-green-100 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Hoy
                            </button>
                        </div>
                    </div>,
                    document.body
                )}
        </div>
    );
}
